import { useState } from 'react';
import { History } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { formatDateLabel } from '@/lib/format';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';

/**
 * マスタ変更履歴ビューア フェーズ1 (docs/design/v05-master-history-viewer-requirements.md §2-3)。
 * `GET {entityRoute}/{id}/history` (バックエンド §2-2) を叩き、一覧→詳細の2段階UIで表示する。
 * 差分ハイライトは対象外 (§4 スコープ外)、そのバージョン単体の値を読み取り専用で見られればよい。
 *
 * ⚠️ 作成者は「ユーザーID」のまま表示する (氏名解決はしない)。既存の DocumentDetailPage.tsx の
 * created_by表示と同じ割り切り。氏名解決には CONTROL_DB を跨いだ追加実装が要り、フェーズ1のスコープを
 * 超えるため見送った (design-expertレビュー指摘、CLAUDE.md §6d 過剰設計回避の原則に基づく判断)。
 */
type HistoryVersion = {
  effective_from: string;
  effective_to: string | null;
  created_at?: string;
  created_by?: string | null;
} & Record<string, unknown>;

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '−';
  if (typeof value === 'boolean') return value ? 'はい' : 'いいえ';
  return String(value);
};

/** 有効期間+現在有効バッジ (一覧行・詳細ヘッダの両方で使う) */
function VersionPeriodLabel({ version }: { version: HistoryVersion }) {
  return (
    <span className="text-sm text-text-body">
      {formatDateLabel(version.effective_from, { withYear: true })}
      {' 〜 '}
      {version.effective_to ? formatDateLabel(version.effective_to, { withYear: true }) : '現在'}
    </span>
  );
}

export function MasterHistoryPanel({
  entityRoute, id, label, fieldLabels, variant = 'icon', triggerLabel = '履歴',
}: {
  /** 例: `/masters/skills`・`/employees` (末尾スラッシュなし) */
  entityRoute: string;
  id: string;
  /** モーダルタイトルに使う対象の呼称 (例: 「スキル」「社員」) */
  label: string;
  /** 詳細表示する列 (snake_case キー) とその表示順・日本語ラベル。effective_from/to・created_at/by は別枠で表示するため含めない */
  fieldLabels: Record<string, string>;
  /** トリガーボタンの見た目。'icon' (既定): 編集/削除と同じ44×44アイコンボタン行に並べる用。
   *  'button': 見出し行に単独で置く用 (例: 「抽象度を管理」ボタンと並ぶ場所)。 */
  variant?: 'icon' | 'button';
  /** variant='button' の時のボタン文言。同じ画面に複数の異なる対象 (例: 社員の「所属」タブと
   *  「基本情報」タブ) がある場合、既定の「履歴」だけでは何の履歴か紛らわしいため上書きできる
   *  (design-expertレビュー指摘、CRITICAL)。variant='icon' は常に aria-label="履歴" のまま。 */
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<HistoryVersion | null>(null);

  const { data, isLoading, error } = useQuery<HistoryVersion[]>({
    queryKey: ['master-history', entityRoute, id],
    queryFn: () => apiFetch<HistoryVersion[]>(`${entityRoute}/${id}/history`),
    enabled: open,
  });
  const versions = data ?? [];

  const close = () => { setOpen(false); setSelected(null); };

  return (
    <>
      {variant === 'icon' ? (
        <button type="button" aria-label="履歴" onClick={() => setOpen(true)}
          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring">
          <History size={14} />
        </button>
      ) : (
        <Button type="button" variant="secondary" size="sm" leftIcon={<History size={14} />} onClick={() => setOpen(true)}>
          {triggerLabel}
        </Button>
      )}
      <Modal open={open} title={selected ? `${label}の変更履歴（詳細）` : `${label}の変更履歴`} onClose={close} size="md">
        {isLoading ? (
          <div className="text-text-sub">読み込み中…</div>
        ) : error ? (
          <div className="text-danger-text text-sm">{error instanceof ApiClientError ? error.message : '履歴の取得に失敗しました'}</div>
        ) : versions.length === 0 ? (
          <EmptyState message="変更履歴がありません" />
        ) : selected ? (
          <div className="space-y-3">
            <button type="button" onClick={() => setSelected(null)} className="text-sm text-misespark-navy font-semibold hover:underline focus-ring rounded">
              ← バージョン一覧に戻る
            </button>
            <dl className="divide-y divide-surface-line border border-surface-border rounded-lg overflow-hidden">
              <div className="flex flex-wrap justify-between gap-2 px-3 py-2 bg-surface-sunken">
                <dt className="text-sm text-text-sub shrink-0">有効期間</dt>
                <dd className="text-sm text-text-body text-right break-words min-w-0"><VersionPeriodLabel version={selected} /></dd>
              </div>
              {selected.created_at && (
                <div className="flex flex-wrap justify-between gap-2 px-3 py-2">
                  <dt className="text-sm text-text-sub shrink-0">作成日時</dt>
                  <dd className="text-sm text-text-body text-right break-words min-w-0">{formatDateLabel(selected.created_at, { withYear: true })}</dd>
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-2 px-3 py-2">
                <dt className="text-sm text-text-sub shrink-0">作成者 (ユーザーID)</dt>
                <dd className="text-sm text-text-body text-right break-all min-w-0">{formatFieldValue(selected.created_by)}</dd>
              </div>
              {Object.entries(fieldLabels).map(([key, fieldLabel]) => (
                <div key={key} className="flex flex-wrap justify-between gap-2 px-3 py-2">
                  <dt className="text-sm text-text-sub shrink-0">{fieldLabel}</dt>
                  <dd className="text-sm text-text-body text-right break-words min-w-0">{formatFieldValue(selected[key])}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <ul className="space-y-2">
            {versions.map((v, i) => (
              <li key={`${v.effective_from}-${i}`}>
                <button
                  type="button"
                  onClick={() => setSelected(v)}
                  className="w-full flex flex-wrap items-center justify-between gap-1.5 border border-surface-border rounded-lg bg-white p-3 text-left hover:bg-surface-sunken focus-ring"
                >
                  <VersionPeriodLabel version={v} />
                  {v.effective_to === null && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap shrink-0">現在有効</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}
